import {useLayoutEffect, useState} from 'react';
import ShadowRoot from 'react-shadow';

export function ShadowDivWithForwardEvents(
  props: Omit<React.HTMLProps<HTMLElement>, 'ref'>,
) {
  const [el, setEl] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!el) {
      return;
    }

    const shadowRoot = el.shadowRoot;
    if (!shadowRoot) {
      return;
    }

    const preventSubmit = (ev: Event) => {
      ev.preventDefault();
    };
    shadowRoot.addEventListener('submit', preventSubmit, {capture: true});

    const cleanupForwardEvents = forwardEvents(shadowRoot, el, [
      'click',
      'mouseover',
      'mouseout',
      'mouseenter',
      'mouseleave',
      'pointerover',
      'pointerout',
      'pointerenter',
      'pointerleave',
    ]);

    return () => {
      shadowRoot.removeEventListener('submit', preventSubmit, {capture: true});
      cleanupForwardEvents();
    };
  }, [el]);

  return <ShadowRoot.div {...props} ref={setEl} />;
}

function forwardEvents(
  from: Node,
  to: HTMLElement,
  events: (keyof HTMLElementEventMap)[],
) {
  const forwardEvent = (ev: Event) => {
    to.dispatchEvent(copyEvent(ev));
  };

  for (const ev of events) {
    from.addEventListener(ev, forwardEvent, {capture: true});
  }

  return () => {
    for (const ev of events) {
      from.removeEventListener(ev, forwardEvent, {capture: true});
    }
  };
}

function copyEvent(originalEvent: Event) {
  // Create a new event based on the type
  let newEvent: Event;

  if (originalEvent instanceof MouseEvent) {
    newEvent = new MouseEvent(originalEvent.type, {
      bubbles: true,
      cancelable: originalEvent.cancelable,
      composed: true,
      button: originalEvent.button,
      buttons: originalEvent.buttons,
      clientX: originalEvent.clientX,
      clientY: originalEvent.clientY,
      ctrlKey: originalEvent.ctrlKey,
      shiftKey: originalEvent.shiftKey,
      altKey: originalEvent.altKey,
      metaKey: originalEvent.metaKey,
    });
  } else if (originalEvent instanceof PointerEvent) {
    newEvent = new PointerEvent(originalEvent.type, {
      bubbles: true,
      cancelable: originalEvent.cancelable,
      composed: true,
      button: originalEvent.button,
      buttons: originalEvent.buttons,
      clientX: originalEvent.clientX,
      clientY: originalEvent.clientY,
      ctrlKey: originalEvent.ctrlKey,
      shiftKey: originalEvent.shiftKey,
      altKey: originalEvent.altKey,
      metaKey: originalEvent.metaKey,
    });
  } else {
    newEvent = new Event(originalEvent.type, {
      bubbles: true,
      cancelable: originalEvent.cancelable,
      composed: true,
    });
  }

  // Copy the target reference to preserve the original element
  Object.defineProperty(newEvent, 'target', {
    value: originalEvent.target,
    enumerable: true,
  });

  return newEvent;
}
